import {reduce} from 'ramda';
import {Step, StepDivider} from '../../../components/step';
import React, {useMemo, useState} from 'react';
import {
  Column,
  Row,
  Cell,
  useFlexLayout,
  useResizeColumns,
  UseResizeColumnsOptions,
  useTable,
  useExpanded,
  UseExpandedInstanceProps,
  UseExpandedRowProps,
} from 'react-table';
import {Company, ManagerAccountState} from '../../../models/company';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {ReactComponent as ExportIcon} from '@common/assets/export_icon.svg';
import {ReactComponent as UserIcon} from '@common/assets/user.svg';
import {ReactComponent as AngleRightIcon} from '@common/assets/angle-right.svg';
import {ReactComponent as DoubleAngleTopIcon} from '@common/assets/double-angle-top.svg';
import {ReactComponent as LinkIcon} from '@common/assets/link.svg';
import {ReactComponent as LockIcon} from '@common/assets/lock.svg';
import {ReactComponent as EditIcon} from '@common/assets/edit.svg';
import {ReactComponent as TrashIcon} from '@common/assets/icon_trash.svg';
import {Table, Tbody, Td, Th, Thead, Tr} from 'components/table';
import {faSpinner} from '@fortawesome/pro-light-svg-icons';
import GenerateCodeForm from './generate-code-form';
import ResetModal from '@common/components/reset-modal';
import {useMutation} from 'react-query';
import {useXlsExport} from 'hooks/useXlsExport';
import {api} from '../../../utils/api';
import {
  Tooltip,
  Button,
  toast,
} from '@mayo/mayo-ui-gakki';

const ManagerAccountStateCell = (cellProps: any) => {
  const {row, value} = cellProps;
  const [isApplied, setIsApplied] = useState(false);
  const {mutateAsync, isLoading} = useMutation(
    () => api.post(`user/company/${row.original.id}/administrator`),
    {
      onSuccess: () => setIsApplied(true),
    },
  );
  const {isFinished} = row.original;
  if (!isFinished) return null;
  if (value === ManagerAccountState.applied || isApplied)
    return <span>已申請</span>;
  return (
    <Tooltip content="申請管理者帳號" isIcon>
      <Button
        variant={'simple'}
        colorSchema={'primary'}
        onClick={() => {
          toast.promise(() => mutateAsync(), {
            pending: 'loading',
            success: `已申請成功`,
          })
        }}
        disabled={isLoading}
        className={'manage-account-btn'}
        icon={<UserIcon className={'w-[20px]'} />}
      />
    </Tooltip>
  );
};

const steps: {step: number; name: string}[] = [
  {step: 1, name: '職稱'},
  {step: 2, name: '人員'},
  {step: 3, name: '組織'},
  {step: 4, name: '上下班/彈性工時'},
  {step: 5, name: '剩餘假別'},
  {step: 6, name: '打卡'},
  {step: 7, name: '排班'},
  {step: 8, name: '發薪'},
  {step: 9, name: '公司'},
  {step: 10, name: '員工'},
  {step: 11, name: '管理員'},
  {step: 12, name: '啟用'},
];

function Steps({currentStep, isEnablePY}: {currentStep: number; isEnablePY: boolean}) {
  let stepComps = reduce(
    (comps, step) => {
      if (!isEnablePY && [8, 9, 10].includes(step.step)) {
        return [...comps];
      }
      let status: 'disable' | 'active' | 'checked' = 'disable';
      if (currentStep === step.step) {
        status = 'active';
      }
      if (currentStep > step.step) {
        status = 'checked';
      }
      return [
        ...comps,
        <Step name={step.name} status={status} key={step.step} />,
        <StepDivider
          key={step.step + '-divider'}
          status={currentStep > step.step ? 'active' : 'disabled'}
        />,
      ];
    },
    [] as React.ReactNode[],
    steps,
  );

  stepComps = stepComps.slice(0, stepComps.length - 1);
  return <div className="flex items-center">{stepComps}</div>;
}

type ColumnProps<T extends object> = Column<T> & UseResizeColumnsOptions<T>;
type CompanyListProps = {
  companies: Company[];
  isLoading?: boolean;
  onEdit?: (data: Company) => void;
  refetchPage?: () => void;
};

function CompanyList({
  companies,
  isLoading = false,
  onEdit = () => {},
  refetchPage = () => {},
}: CompanyListProps) {
  const [resetPasswordData, setResetPasswordData] = React.useState<{
    companyId: string;
    companyCode: string;
    vat: string;
  } | null>(null);
  const [currentDeleteData, setCurrentDeleteData] = React.useState<Company | null>(null);
  const {mutateAsync: resetPassword} = useMutation(
    (data: {
      companyId: string;
      companyCode: string;
      vat: string;
    }): Promise<void> => {
      return api.put(
        `backoffice/company/${data.companyId}/account/password/reset?companyCode=${data.companyCode}&vat=${data.vat}`,
      );
    },
    {
      onSuccess: () => {
        setResetPasswordData(null);
      },
    },
  );
  const {mutateAsync: deleteCompany, isLoading: isDeleting} = useMutation(
    (companyId: string): Promise<void> => {
      return api.delete(
        `BackOffice/company/MongoData`,
        {
          data: {
            companyIds: [companyId],
          },
        },
      );
    },
    {
      onSuccess: () => {
        setCurrentDeleteData(null);
        refetchPage();
      },
    },
  );

  const {setData: exportExcelData} = useXlsExport();
  const columns = useMemo(() => {
    let columns: ColumnProps<Company>[] = [
      {
        Header: '公司名稱',
        accessor: 'name',
        width: 200,
        maxWidth: 200,
        Cell: ({value, row}) => (
          <span data-test-id={`company-name-${row.index}`}>{value}</span>
        ),
      },
      {
        Header: '統一編號',
        accessor: 'vat',
        width: 120,
        maxWidth: 120,
        disableResizing: true,
        Cell: ({value, row}) => (
          <span data-test-id={`company-vat-${row.index}`}>{value}</span>
        ),
      },
      {
        Header: '購買授權人數',
        accessor: 'amountOfLicense',
        width: 120,
        maxWidth: 120,
        disableResizing: true,
        Cell: ({value, row}) => (
          <span data-test-id={`number-of-license-${row.index}`}>{value}</span>
        ),
      },
      {
        Header: '實際導入人數',
        accessor: 'amountOfEmployee',
        width: 120,
        maxWidth: 120,
        disableResizing: true,
        Cell: ({value, row}) => (
          <span data-test-id={`number-of-employee-${row.index}`}>{value}</span>
        ),
      },
      {
        // Build our expander column
        id: 'expander', // Make sure it has an ID
        accessor: 'progressState',
        Header: (cell: Cell<object> & UseExpandedInstanceProps<object>) => {
          return (
            <span className="flex items-center gap-1">
              導入進程
              <Tooltip content="展開導入進程" isIcon>
                <Button
                  variant={'simple'}
                  colorSchema={'primary'}
                  onClick={() => cell.toggleAllRowsExpanded(!cell.isAllRowsExpanded)}
                  data-test-id={`expand-all-button`}
                  icon={
                    <DoubleAngleTopIcon
                      className={`transition-all ${
                        cell.isAllRowsExpanded ? 'rotate-180' : 'rotate-0'
                      }`}
                    />
                  }
                />
              </Tooltip>
            </span>
          );
        },
        Cell: cell => {
          const row = cell.row as Row<Company> & UseExpandedRowProps<Company>;
          const isEnablePY = row.original.feature.enablePY;
          let progressState = cell.value;
          if (!isEnablePY && [8, 9, 10].includes(progressState)) {
            progressState = 11;
          }
          return (
            <span
              className={`flex items-center gap-1 ${
                row.isExpanded ? 'text-[#26abe3]' : ''
              }`}
            >
              <Button
                {...row.getToggleRowExpandedProps()}
                variant={'simple'}
                colorSchema={'primary'}
                data-test-id={`expand-button-${row.index}`}
                icon={
                  <AngleRightIcon
                    className={`transition-all ${
                      row.isExpanded ? 'rotate-90' : 'rotate-0'
                    }`}
                  />
                }
              />
              {steps[progressState - 1]?.name || '尚未開始'}
            </span>
          );
        },
      },
      {
        Header: '功能',
        id: 'functions',
        accessor: 'managerAccountState',
        width: 240,
        maxWidth: 240,
        disableResizing: true,
        //@ts-ignore
        Cell: cellProps => {
          const {row} = cellProps;
          const {isFinished} = row.original;
          const [isOpen, setIsOpen] = useState(false);
          return (
            <div className="w-full flex items-center justify-start gap-4">
              {/* 登入連結 */}
              <GenerateCodeForm
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                companyId={row.id}
              />
              <Tooltip content="登入連結" isIcon>
                <Button
                  variant={'simple'}
                  colorSchema={'primary'}
                  onClick={() => setIsOpen(true)}
                  className={'login-link'}
                  data-test-id={`login-link-button-${row.index}`}
                  icon={<LinkIcon className={'w-[20px]'} />}
                />
              </Tooltip>
              {/* 重設密碼 */}
              <Tooltip content="重設密碼" isIcon>
                <Button
                  variant={'simple'}
                  colorSchema={'primary'}
                  onClick={() =>
                    setResetPasswordData({
                      companyId: row.original.id,
                      companyCode: row.original.code,
                      vat: row.original.vat,
                    })
                  }
                  className={'unlock-btn'}
                  icon={<LockIcon className={'w-[20px]'} />}
                />
              </Tooltip>
              {/* 編輯 */}
              <Tooltip content="編輯" isIcon>
                <Button
                  variant={'simple'}
                  colorSchema={'primary'}
                  onClick={() => onEdit(row.original)}
                  className={'edit-btn'}
                  icon={<EditIcon className={'w-[20px]'} />}
                />
              </Tooltip>
              {/* 刪除導入專區 */}
              {!isFinished && (
                <Tooltip content="刪除導入專區" isIcon>
                  <Button
                    variant={'simple'}
                    colorSchema={'primary'}
                    onClick={() => setCurrentDeleteData(row.original)}
                    className={'delete-btn'}
                    icon={<TrashIcon className={'w-[20px]'} />}
                  />
                </Tooltip>
              )}
              {/* 設定參數報表 */}
              {isFinished && (
                <Tooltip content="設定參數報表" isIcon>
                  <Button
                    variant={'simple'}
                    colorSchema={'primary'}
                    onClick={async () => {
                      const res = await api.get(
                        `backoffice/report/excel/${row.original.id}`,
                        {responseType: 'blob'},
                      );
                      exportExcelData({
                        blob: res,
                        name: `GakkiReport-${
                          row.original.code
                        }-${Date.now()}.xls`,
                      });
                    }}
                    className={'export-btn'}
                    icon={<ExportIcon className={'w-[20px]'} />}
                  />
                </Tooltip>
              )}
              <ManagerAccountStateCell {...cellProps} />
            </div>
          );
        },
      },
    ];
    return columns;
  }, [onEdit, exportExcelData]);
  const {rows, prepareRow, getTableBodyProps, getTableProps, headerGroups} =
    useTable<Company>(
      {
        columns,
        data: companies,
        getRowId: row => row.id,
      },
      useFlexLayout,
      useResizeColumns,
      useExpanded,
    );

  return (
    <>
      <Table {...getTableProps()}>
        <Thead className="z-50 sticky top-0">
          {headerGroups.map((headerGroup, idx) => (
            <Tr
              {...headerGroup.getHeaderGroupProps()}
              className="relative bg-white"
              key={`th-tr-${idx}`}
            >
              {headerGroup.headers.map((column, cidx) => (
                <Th
                  {...column.getHeaderProps({
                    style: {maxWidth: column.maxWidth, width: column.width},
                  })}
                  key={`th-td-${cidx}`}
                >
                  <div className="flex items-center h-full text-sm">
                    {column.render('Header')}
                  </div>
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody {...getTableBodyProps()}>
          {isLoading && (
            <div className={'p-3 text-center'}>
              <FontAwesomeIcon icon={faSpinner} pulse size={'2x'} />
            </div>
          )}
          {!isLoading &&
            rows.map((row: any, idx) => {
              prepareRow(row);
              const rowProps = row.getRowProps();
              const isEnablePY = row.original.feature.enablePY;
              let progressState = row.original.progressState;
              if (!isEnablePY && [8, 9, 10].includes(progressState)) {
                progressState = 11;
              }
              return (
                <React.Fragment key={`tb-group-${idx}`}>
                  <Tr {...rowProps} className="z-20 relative bg-white">
                    {row.cells.map((cell: any, cellidx: number) => {
                      return (
                        <Td
                          {...cell.getCellProps({
                            style: {
                              maxWidth: cell.column.maxWidth,
                              width: cell.column.width,
                            },
                          })}
                          key={`tb-group-td${cellidx}`}
                        >
                          <div className="flex items-center h-full text-sm">
                            {cell.render('Cell')}
                          </div>
                        </Td>
                      );
                    })}
                  </Tr>
                  <Tr
                    {...rowProps}
                    key={`expand-row-${row.id}`}
                    className={`z-10 w-full origin-top transition-all ${
                      row.isExpanded ? 'h-[40px]' : 'scale-y-0 h-0'
                    }`}
                  >
                    <Td className="w-full bg-[#f9f9f9] !px-8">
                      <Steps
                        currentStep={progressState}
                        isEnablePY={isEnablePY}
                      />
                    </Td>
                  </Tr>
                </React.Fragment>
              );
            })}
          {!isLoading && rows.length === 0 && (
            <Tr>
              <Td className="text-center">查無資料</Td>
            </Tr>
          )}
        </Tbody>
      </Table>
      <ResetModal
        onConfirm={async () => {
          if (!currentDeleteData) {
            return;
          }
          toast.promise(() => deleteCompany(currentDeleteData.id), {
            pending: 'loading',
            success: `專區刪除成功`,
          })
        }}
        onCancel={() => setCurrentDeleteData(null)}
        isOpen={currentDeleteData !== null}
        title={'刪除導入專區'}
        disabled={isDeleting}
        text={
          <p>
            您正在刪除專區<b className="whitespace-nowrap">{` ${currentDeleteData?.name}(${currentDeleteData?.vat})`}</b>
            <br />
            刪除後導入專區資料將不可復原
            <br />
            是否確定刪除？
          </p>
        }
      />
      <ResetModal
        onConfirm={async () => {
          if (!resetPasswordData) {
            return;
          }
          toast.promise(() => resetPassword(resetPasswordData), {
            pending: 'loading',
            success: `重置密碼成功`,
          })
        }}
        onCancel={() => setResetPasswordData(null)}
        isOpen={resetPasswordData !== null}
        title={'重置密碼'}
        text={
          <p>
            重置後，密碼將被設定為 [公司代碼]+[統一編號]
            <br />
            請問是否確定重置?
          </p>
        }
      />
    </>
  );
}

export default CompanyList;
